import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { EmployeeAuthService } from '../services/employee-auth.service';
import { AutoLoginService } from '../services/auto-login.service';
import { StorageService } from '../services/storage.service';
import { of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

/**
 * Guard para verificar autenticación de empleado
 * Soporta auto-login desde owner mediante query param
 * Redirige a login de empleado si no está autenticado
 */
export const employeeAuthGuard: CanActivateFn = (route, state) => {
  const employeeAuthService = inject(EmployeeAuthService);
  const autoLoginService = inject(AutoLoginService);
  const storageService = inject(StorageService);
  const router = inject(Router);

  // ── Flujo 1: employee login token directo (viene del onboarding) ──────────
  // El token ya es un employee token válido — solo guardarlo y verificar.
  const elt = route.queryParams['elt'];
  if (elt) {
    storageService.setEmployeeToken(elt);
    return employeeAuthService.getProfile().pipe(
      map(() => true),
      catchError(() => {
        storageService.removeEmployeeToken();
        return of(router.createUrlTree(['/employee/login'], {
          queryParams: { returnUrl: state.url.split('?')[0] }
        }));
      })
    );
  }

  // ── Flujo 2: owner token exchange (viene del selectBusiness legado) ────────
  const autoLoginToken = route.queryParams['autoLoginToken'];
  if (autoLoginToken) {
    return autoLoginService.processAutoLogin(autoLoginToken).pipe(
      map(response => response.success
        ? true
        : router.createUrlTree(['/employee/login'], { queryParams: { returnUrl: state.url.split('?')[0] } })
      ),
      catchError(() => of(router.createUrlTree(['/employee/login'], {
        queryParams: { returnUrl: state.url.split('?')[0] }
      })))
    );
  }

  console.log('🔒 No hay autoLoginToken, verificando autenticación normal...');
  
  const isAuth = employeeAuthService.isAuthenticated();
  const token = storageService.getEmployeeToken();
  const employee = employeeAuthService.getCurrentEmployee();
  
  console.log('📊 Estado de autenticación:');
  console.log('  - isAuthenticated():', isAuth);
  console.log('  - Token en storage:', token ? token.substring(0, 20) + '...' : 'null');
  console.log('  - Employee actual:', employee);
  
  // Flujo normal: verificar autenticación
  if (!isAuth) {
    console.log('⚠️ No autenticado, redirigiendo a login');
    // Guardar la URL solicitada para redirigir después del login
    return router.createUrlTree(['/employee/login'], {
      queryParams: { returnUrl: state.url }
    });
  }

  // Si tiene token pero no tiene empleado cargado, cargar el perfil
  if (!employee) {
    console.log('🔄 Token válido pero empleado no cargado, obteniendo perfil...');
    
    return employeeAuthService.getProfile().pipe(
      map(() => {
        console.log('✅ Perfil cargado correctamente');
        return true;
      }),
      catchError((error) => {
        console.error('❌ Error al cargar perfil:', error);
        
        // Si el token es inválido, limpiar sesión y redirigir
        if (error.status === 401) {
          console.log('⚠️ Token inválido, limpiando sesión...');
          employeeAuthService.clearSession();
        }
        
        return of(router.createUrlTree(['/employee/login'], {
          queryParams: { returnUrl: state.url }
        }));
      })
    );
  }

  console.log('✅ Empleado autenticado correctamente');
  return true;
};
